import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    useTheme,
    alpha
} from '@mui/material'
import { Send as SendIcon, Email as EmailIcon, Schedule as ScheduleIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

interface ContactFormData {
    name: string
    email: string
    subject: string
    message: string
}

const Contact: React.FC = () => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<ContactFormData>({
        defaultValues: {
            name: '',
            email: '',
            subject: '',
            message: ''
        }
    })

    const onSubmit = async (data: ContactFormData) => {
        setIsSubmitting(true)
        setSubmitStatus('idle')

        try {
            // Simulate API call - replace with actual implementation
            await new Promise(resolve => setTimeout(resolve, 2000))

            // For now, we'll just log the data and show success
            console.log('Contact form data:', data)

            setSubmitStatus('success')
            reset()
        } catch (error) {
            console.error('Error submitting contact form:', error)
            setSubmitStatus('error')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography
                    variant="h2"
                    component="h1"
                    gutterBottom
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 800
                    }}
                >
                    {t('contact.title')}
                </Typography>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                    {t('contact.subtitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                    {t('contact.description')}
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Contact Form */}
                <Grid item xs={12} md={8}>
                    <Card
                        elevation={0}
                        sx={{
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            backdropFilter: 'blur(10px)',
                            borderRadius: 3
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                                {t('contact.form.name')}
                            </Typography>

                            {submitStatus === 'success' && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    {t('contact.form.success')}
                                </Alert>
                            )}

                            {submitStatus === 'error' && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {t('contact.form.error')}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                                <Grid container spacing={3}>
                                    {/* Name Field */}
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{ required: t('contact.validation.nameRequired') }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label={t('contact.form.name')}
                                                    placeholder={t('contact.form.namePlaceholder')}
                                                    error={!!errors.name}
                                                    helperText={errors.name?.message}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.primary.main
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Email Field */}
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="email"
                                            control={control}
                                            rules={{
                                                required: t('contact.validation.emailRequired'),
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: t('contact.validation.emailInvalid')
                                                }
                                            }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    type="email"
                                                    label={t('contact.form.email')}
                                                    placeholder={t('contact.form.emailPlaceholder')}
                                                    error={!!errors.email}
                                                    helperText={errors.email?.message}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.primary.main
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Subject Field */}
                                    <Grid item xs={12}>
                                        <Controller
                                            name="subject"
                                            control={control}
                                            rules={{ required: t('contact.validation.subjectRequired') }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label={t('contact.form.subject')}
                                                    placeholder={t('contact.form.subjectPlaceholder')}
                                                    error={!!errors.subject}
                                                    helperText={errors.subject?.message}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.primary.main
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Message Field */}
                                    <Grid item xs={12}>
                                        <Controller
                                            name="message"
                                            control={control}
                                            rules={{
                                                required: t('contact.validation.messageRequired'),
                                                minLength: {
                                                    value: 10,
                                                    message: t('contact.validation.messageMinLength')
                                                }
                                            }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={6}
                                                    label={t('contact.form.message')}
                                                    placeholder={t('contact.form.messagePlaceholder')}
                                                    error={!!errors.message}
                                                    helperText={errors.message?.message}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.primary.main
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Submit Button */}
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={isSubmitting}
                                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                                            sx={{
                                                py: 1.5,
                                                px: 4,
                                                borderRadius: 2,
                                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                fontSize: '1.1rem',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                                                    transform: 'translateY(-2px)'
                                                },
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                        >
                                            {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            height: 'fit-content',
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                            backdropFilter: 'blur(10px)',
                            borderRadius: 3
                        }}
                    >
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                            {t('contact.info.title')}
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <EmailIcon color="primary" />
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {t('contact.info.email')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        hallo@squashpat.de
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ScheduleIcon color="primary" />
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {t('contact.info.responseTime')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {t('contact.info.responseTimeValue')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            Ich antworte normalerweise sehr schnell auf Nachrichten. Falls du eine dringende Anfrage hast,
                            kannst du mich auch direkt per E-Mail kontaktieren.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    )
}

export default Contact
